import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PublicNav from './components/PublicNav';
import Footer from './components/Footer';

function getPublicUserId() {
  try {
    const raw = localStorage.getItem('userData') || localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw)?.id || null;
  } catch {
    return null;
  }
}

function LegalBreadcrumb({ isPrivacy }) {
  const linkClass = 'text-gray-600 hover:text-gray-900 hover:underline';
  const activeClass = 'font-medium text-gray-900';
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6 text-sm">
      <Link to="/" className="font-medium text-green-700 hover:text-green-900 hover:underline">
        Home
      </Link>
      <span className="text-gray-300" aria-hidden>
        |
      </span>
      {isPrivacy ? (
        <Link to="/terms" className={linkClass}>
          Terms of Service
        </Link>
      ) : (
        <span className={activeClass}>Terms of Service</span>
      )}
      <span className="text-gray-300" aria-hidden>
        |
      </span>
      {isPrivacy ? (
        <span className={activeClass}>Privacy Policy</span>
      ) : (
        <Link to="/privacy" className={linkClass}>
          Privacy Policy
        </Link>
      )}
    </div>
  );
}

export default function TermsOfService() {
  const currentUserId = getPublicUserId();
  const location = useLocation();
  const isPrivacy = location.pathname === '/privacy';
  const effectiveDate = new Date().toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  useEffect(() => {
    document.title = isPrivacy ? 'Privacy Policy | PlantaDay' : 'Terms of Service | PlantaDay';
  }, [isPrivacy]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNav currentUserId={currentUserId} />
      <div className="max-w-4xl mx-auto px-4 py-8 w-full flex-1">
        <LegalBreadcrumb isPrivacy={isPrivacy} />

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 md:p-8">
          {isPrivacy ? (
            <>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">Effective Date: {effectiveDate}</p>

              <div className="prose prose-sm sm:prose-base md:prose-lg max-w-none">
                <p className="text-gray-700 mb-6">
                  Your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, and
                  safeguard your information when you use PlantaDay (&quot;the Service&quot;), in compliance with the
                  Personal Information Protection and Electronic Documents Act (PIPEDA) and British Columbia&apos;s
                  Personal Information Protection Act (PIPA).
                </p>

                <section className="mb-8">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">
                    1. Information We Collect
                  </h2>
                  <p className="text-gray-700 mb-4">We collect information that you provide directly to us, including:</p>
                  <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                    <li>Account information (name, email, password, location)</li>
                    <li>Profile information (business name, phone, address, bio)</li>
                    <li>Content you submit (plant information, photos, designs)</li>
                    <li>Communications with us and other users</li>
                  </ul>
                  <p className="text-gray-700 mb-4">We also automatically collect certain information when you use the Service, including:</p>
                  <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                    <li>Device information and IP address</li>
                    <li>Usage data and analytics</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">2. How We Use Your Information</h2>
                  <p className="text-gray-700 mb-4">We use the information we collect to:</p>
                  <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                    <li>Provide, maintain, and improve the Service</li>
                    <li>Process transactions and send related information</li>
                    <li>Send technical notices, updates, and support messages</li>
                    <li>Respond to your comments and questions</li>
                    <li>Monitor and analyze usage patterns</li>
                    <li>Detect, prevent, and address technical issues</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">3. Information Sharing and Disclosure</h2>
                  <p className="text-gray-700 mb-4">We do not sell your personal information. We may share your information in the following circumstances:</p>
                  <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                    <li>With your consent</li>
                    <li>To service providers who assist in operating the Service</li>
                    <li>When required by law or to protect rights and safety</li>
                    <li>In connection with a business transfer or merger</li>
                    <li>Public profile information (name, business, location) may be visible to other users</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">4. Data Security</h2>
                  <p className="text-gray-700 mb-4">
                    We implement appropriate technical and organizational measures to protect your personal information.
                    However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute
                    security.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">5. Your Rights</h2>
                  <p className="text-gray-700 mb-4">Under Canadian privacy laws, you have the right to:</p>
                  <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                    <li>Access your personal information</li>
                    <li>Request correction of inaccurate information</li>
                    <li>Request deletion of your information</li>
                    <li>Withdraw consent for certain uses</li>
                    <li>
                      File a complaint with the Privacy Commissioner of Canada or BC Information and Privacy Commissioner
                    </li>
                  </ul>
                  <p className="text-gray-700 mb-4">To exercise these rights, please contact us at the email address provided below.</p>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">6. Cookies and Tracking</h2>
                  <p className="text-gray-700 mb-4">
                    We use cookies and similar tracking technologies to track activity on our Service and hold certain
                    information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being
                    sent.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">7. Third-Party Services</h2>
                  <p className="text-gray-700 mb-4">
                    Our Service may contain links to third-party websites or services. We are not responsible for the
                    privacy practices of these third parties. We encourage you to read their privacy policies.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">8. Children&apos;s Privacy</h2>
                  <p className="text-gray-700 mb-4">
                    Our Service is not intended for children under the age of 13. We do not knowingly collect personal
                    information from children under 13. If you are a parent or guardian and believe your child has
                    provided us with personal information, please contact us.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">9. Changes to This Privacy Policy</h2>
                  <p className="text-gray-700 mb-4">
                    We may update our Privacy Policy from time to time. We will notify you of any changes by posting the
                    new Privacy Policy on this page and updating the &quot;Effective Date&quot; at the top.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">10. Contact</h2>
                  <p className="text-gray-700 mb-4">If you have questions about this Privacy Policy, please contact us at:</p>
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <p className="text-gray-700">
                      <strong>PlantaDay</strong>
                      <br />
                      Email: support@plantaday.com
                      <br />
                      Address: British Columbia, Canada
                    </p>
                  </div>
                </section>

                <div className="border-t border-gray-200 pt-6 mt-8">
                  <p className="text-sm text-gray-500">Last updated: {effectiveDate}</p>
                </div>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">Effective Date: {effectiveDate}</p>

              <div className="prose prose-sm sm:prose-base md:prose-lg max-w-none">
                <section className="mb-6 sm:mb-8">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">1. Acceptance of Terms</h2>
                  <p className="text-gray-700 mb-4">
                    By accessing or using PlantaDay (&quot;the Service&quot;), you agree to be bound by these Terms of Service
                    and all applicable laws and regulations of British Columbia, Canada. If you do not agree with any of
                    these terms, you are prohibited from using or accessing this Service.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
                  <p className="text-gray-700 mb-4">
                    PlantaDay is a plant database and garden design platform that connects landscape designers with
                    homeowners. The Service includes:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                    <li>Access to a comprehensive plant database</li>
                    <li>AI-powered plant discovery and search tools</li>
                    <li>Designer profiles and matching services</li>
                    <li>Yard analysis and design recommendations</li>
                    <li>Community features for sharing plant information</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">3.1 Account Creation</h3>
                  <p className="text-gray-700 mb-4">To use certain features of the Service, you must create an account. You agree to:</p>
                  <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                    <li>Provide accurate, current, and complete information</li>
                    <li>Maintain and update your information to keep it accurate</li>
                    <li>Maintain the security of your password and account</li>
                    <li>Accept responsibility for all activities under your account</li>
                  </ul>

                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">3.2 Account Types</h3>
                  <p className="text-gray-700 mb-4">
                    PlantaDay offers different account types including Plant Contributors and Designers. Each account type
                    has different features and responsibilities as described in the Service.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. User Content and Conduct</h2>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 User-Generated Content</h3>
                  <p className="text-gray-700 mb-4">
                    You retain ownership of any content you submit, post, or display on the Service (&quot;User Content&quot;). By
                    submitting User Content, you grant PlantaDay a worldwide, non-exclusive, royalty-free license to use,
                    reproduce, modify, adapt, publish, and distribute such content for the purpose of operating and
                    promoting the Service.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Prohibited Conduct</h3>
                  <p className="text-gray-700 mb-4">You agree not to:</p>
                  <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                    <li>Violate any applicable laws or regulations</li>
                    <li>Infringe upon the rights of others, including intellectual property rights</li>
                    <li>Post false, misleading, or defamatory content</li>
                    <li>Upload malicious code, viruses, or harmful software</li>
                    <li>Impersonate any person or entity</li>
                    <li>Harass, abuse, or harm other users</li>
                    <li>Use the Service for any illegal or unauthorized purpose</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Intellectual Property</h2>
                  <p className="text-gray-700 mb-4">
                    The Service, including its original content, features, and functionality, is owned by PlantaDay and is
                    protected by Canadian and international copyright, trademark, patent, trade secret, and other
                    intellectual property laws.
                  </p>
                  <p className="text-gray-700 mb-4">
                    Plant information, images, and other content may be contributed by users or sourced from public
                    databases. You agree to respect the intellectual property rights of all content contributors.
                  </p>
                </section>

                <section className="mb-8 rounded-lg bg-green-50 border border-green-100 p-4 sm:p-5">
                  <p className="text-gray-800 mb-0">
                    How we collect, use, and protect personal information is described in our{' '}
                    <Link to="/privacy" className="text-green-800 font-semibold hover:underline">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Disclaimers and Limitations of Liability</h2>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Service Availability</h3>
                  <p className="text-gray-700 mb-4">
                    The Service is provided &quot;as is&quot; and &quot;as available&quot; without warranties of any kind, either express or
                    implied. We do not guarantee that the Service will be uninterrupted, secure, or error-free.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 Plant Information</h3>
                  <p className="text-gray-700 mb-4">
                    Plant information, recommendations, and AI-generated content are provided for informational purposes
                    only. We do not guarantee the accuracy, completeness, or suitability of any plant information.
                    Always consult with qualified professionals before making landscaping decisions.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-900 mb-3">6.3 Limitation of Liability</h3>
                  <p className="text-gray-700 mb-4">
                    To the maximum extent permitted by British Columbia law, PlantaDay shall not be liable for any
                    indirect, incidental, special, consequential, or punitive damages, or any loss of profits or
                    revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other
                    intangible losses resulting from your use of the Service.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Indemnification</h2>
                  <p className="text-gray-700 mb-4">
                    You agree to indemnify, defend, and hold harmless PlantaDay, its officers, directors, employees, and
                    agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable
                    legal fees, arising out of or in any way connected with your use of the Service, your violation of
                    these Terms, or your violation of any rights of another.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Termination</h2>
                  <p className="text-gray-700 mb-4">
                    We may terminate or suspend your account and access to the Service immediately, without prior notice
                    or liability, for any reason, including if you breach these Terms. Upon termination, your right to
                    use the Service will cease immediately.
                  </p>
                  <p className="text-gray-700 mb-4">
                    You may terminate your account at any time by contacting us or using account deletion features in the
                    Service.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Governing Law</h2>
                  <p className="text-gray-700 mb-4">
                    These Terms shall be governed by and construed in accordance with the laws of British Columbia,
                    Canada, without regard to its conflict of law provisions. Any disputes arising from these Terms or the
                    Service shall be subject to the exclusive jurisdiction of the courts of British Columbia.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Changes to Terms</h2>
                  <p className="text-gray-700 mb-4">
                    We reserve the right to modify or replace these Terms at any time. If a revision is material, we will
                    provide at least 30 days notice prior to any new terms taking effect. What constitutes a material
                    change will be determined at our sole discretion.
                  </p>
                  <p className="text-gray-700 mb-4">
                    By continuing to access or use the Service after any revisions become effective, you agree to be
                    bound by the revised terms.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Contact Information</h2>
                  <p className="text-gray-700 mb-4">
                    If you have any questions about these Terms of Service, please contact us at:
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <p className="text-gray-700">
                      <strong>PlantaDay</strong>
                      <br />
                      Email: support@plantaday.com
                      <br />
                      Address: British Columbia, Canada
                    </p>
                  </div>
                  <p className="text-gray-700 mb-4">
                    For privacy-specific questions, see our{' '}
                    <Link to="/privacy" className="text-green-700 font-medium hover:underline">
                      Privacy Policy
                    </Link>{' '}
                    and the contact section there.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Severability</h2>
                  <p className="text-gray-700 mb-4">
                    If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining
                    provisions of these Terms will remain in effect. These Terms constitute the entire agreement between
                    you and PlantaDay regarding the Service.
                  </p>
                </section>

                <div className="border-t border-gray-200 pt-6 mt-8">
                  <p className="text-sm text-gray-500">Last updated: {effectiveDate}</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
